import React from "react";

const CHART_HEIGHT = 150;

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  return formatter.format(value);
};

const BarChart = ({ scenarios, cmTarget }) => {
  const values = scenarios.map((s) => s.showProfit);
  const maxPositive = Math.max(...values, cmTarget * 1.1, 1);
  const minNegative = Math.min(...values, 0);
  const totalRange = maxPositive - minNegative;
  const positiveZone = (maxPositive / totalRange) * CHART_HEIGHT;
  const negativeZone = (Math.abs(minNegative) / totalRange) * CHART_HEIGHT;
  const barHeight = (val) => Math.max(4, (Math.abs(val) / totalRange) * CHART_HEIGHT);
  const targetY = cmTarget > 0 ? (cmTarget / totalRange) * CHART_HEIGHT : 0;

  const barColor = (s) =>
    s.hitsCM ? "var(--bk-c-28a745, #28a745)" : s.showProfit > 0 ? "var(--bk-c-ffc107, #ffc107)" : "var(--bk-c-ff595e, #FF595E)";

  return (
    <div>
      {cmTarget > 0 && (
        <div className="small text-muted mb-3">
          CM Target: {formatCurrency(cmTarget)} per show
        </div>
      )}
      <div style={{ position: "relative" }}>
        {cmTarget > 0 && (
          <div
            style={{
              position: "absolute",
              top: 20 + positiveZone - targetY,
              left: 0,
              right: 0,
              borderTop: "2px dashed var(--bk-c-1982c4, #1982C4)",
              zIndex: 2,
            }}
          >
            <span
              style={{
                position: "absolute",
                right: 0,
                top: -18,
                color: "var(--bk-c-1982c4, #1982C4)",
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            >
              CM Target
            </span>
          </div>
        )}

        {negativeZone > 0 && (
          <div
            style={{
              position: "absolute",
              top: positiveZone + 20,
              left: 0,
              right: 0,
              borderTop: "1px solid var(--bk-c-999, #999)",
              zIndex: 2,
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 0,
                top: -16,
                color: "var(--bk-c-999, #999)",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              $0
            </span>
          </div>
        )}

        <div className="d-flex justify-content-around" style={{ marginLeft: negativeZone > 0 ? 24 : 0 }}>
          {scenarios.map((s) => {
            const h = barHeight(s.showProfit);
            const isNeg = s.showProfit < 0;
            const color = barColor(s);

            return (
              <div
                key={s.label}
                className="text-center"
                style={{ flex: "1 1 0", padding: "0 4px" }}
              >
                <div style={{
                  height: positiveZone + 20,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "stretch",
                }}>
                  {!isNeg && (
                    <div style={{
                      fontSize: "0.75rem",
                      fontWeight: s.isExpected ? 700 : 400,
                      color: color,
                      marginBottom: 4,
                    }}>
                      {formatCurrency(s.showProfit)}
                    </div>
                  )}
                  {isNeg && <div style={{ flex: 1 }} />}
                  {!isNeg && (
                    <div style={{
                      height: h,
                      backgroundColor: color,
                      borderRadius: "4px 4px 0 0",
                      opacity: s.isExpected ? 1 : 0.6,
                      border: s.isExpected ? "2px solid rgba(0,0,0,0.15)" : "none",
                      transition: "height 0.3s ease",
                    }} />
                  )}
                </div>

                <div style={{
                  height: negativeZone > 0 ? negativeZone : 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "stretch",
                }}>
                  {isNeg && (
                    <div style={{
                      height: h,
                      backgroundColor: color,
                      borderRadius: "0 0 4px 4px",
                      opacity: s.isExpected ? 1 : 0.6,
                      border: s.isExpected ? "2px solid rgba(0,0,0,0.15)" : "none",
                      transition: "height 0.3s ease",
                    }} />
                  )}
                </div>

                {isNeg && (
                  <div style={{
                    fontSize: "0.75rem",
                    fontWeight: s.isExpected ? 700 : 400,
                    color: color,
                    marginTop: 4,
                  }}>
                    {formatCurrency(s.showProfit)}
                  </div>
                )}
                <div style={{ fontSize: "0.7rem", color: "var(--bk-c-888, #888)", marginTop: 4 }}>
                  {s.attendance.toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const rows = [
  { key: "attendance", label: "Attendance", format: (v) => v.toLocaleString() },
  { key: "grossPotential", label: "Gross Potential", format: formatCurrency },
  { key: "ancillaryIncome", label: "Ancillary Income", format: formatCurrency },
  { key: "totalRevenue", label: "Total Revenue", format: formatCurrency, bold: true, className: "border-top" },
  { key: "divider1", isDivider: true },
  { key: "beforeAdjustedDeductions", label: "Before Adjusted Deductions", format: formatCurrency, negative: true },
  { key: "artistPayout", label: "Artist Payout", format: formatCurrency, negative: true },
  { key: "expenses", label: "Expenses", format: formatCurrency, negative: true },
  { key: "ancillaryExpenses", label: "Ancillary Expenses", format: formatCurrency, negative: true },
  { key: "divider2", isDivider: true },
  { key: "showProfit", label: "Show Profit", format: formatCurrency, bold: true, colored: true },
  { key: "promoterProfit", label: "Promoter Profit (within Show Profit)", format: formatCurrency },
  { key: "cmDelta", label: "vs. CM Target", format: formatCurrency, colored: true },
];

const ProFormaScenarioTable = ({ scenarios, cmTarget }) => {
  const enrichedScenarios = scenarios.map(s => ({
    ...s,
    cmDelta: s.showProfit - cmTarget,
    hitsCM: s.showProfit >= cmTarget,
  }));

  const visibleRows = cmTarget > 0
    ? rows
    : rows.filter(r => r.key !== 'cmDelta');

  return (
    <div>
      <div className="table-responsive">
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th
                className="border-0 text-muted small"
                style={{ width: "30%" }}
              ></th>
              {enrichedScenarios.map((s) => (
                <th
                  key={s.label}
                  className="border-0 text-center small"
                  style={{
                    fontWeight: s.isExpected ? 700 : 400,
                    color: s.isExpected ? "var(--bk-c-333, #333)" : "var(--bk-c-888, #888)",
                    backgroundColor: s.isExpected
                      ? "rgba(25,130,196,0.05)"
                      : "transparent",
                    borderRadius: s.isExpected ? "8px 8px 0 0" : 0,
                  }}
                >
                  {s.isExpected && (
                    <span
                      className="badge mb-1 d-block"
                      style={{
                        backgroundColor: "var(--bk-c-1982c4, #1982C4)",
                        color: "var(--bk-c-fff, #fff)",
                        fontSize: "0.7rem",
                      }}
                    >
                      YOUR FORECAST
                    </span>
                  )}
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              if (row.isDivider) {
                return (
                  <tr key={row.key}>
                    <td
                      colSpan={5}
                      style={{
                        height: 1,
                        padding: 0,
                        borderTop: "2px solid var(--bk-c-dee2e6, #dee2e6)",
                      }}
                    ></td>
                  </tr>
                );
              }

              return (
                <tr key={row.key} className={row.className || ""}>
                  <td
                    className="small"
                    style={{
                      fontWeight: row.bold ? 600 : 400,
                      paddingTop: 8,
                      paddingBottom: 8,
                    }}
                  >
                    {row.negative && (
                      <span className="text-muted mr-1">&minus;</span>
                    )}
                    {row.label}
                  </td>
                  {enrichedScenarios.map((s) => {
                    const val = s[row.key];
                    let color = "var(--bk-c-333, #333)";
                    if (row.colored) {
                      if (row.key === "showProfit") {
                        color =
                          cmTarget > 0 && val >= cmTarget
                            ? "var(--bk-c-28a745, #28a745)"
                            : val > 0
                            ? (cmTarget > 0 ? "var(--bk-c-ffc107, #ffc107)" : "var(--bk-c-28a745, #28a745)")
                            : "var(--bk-c-ff595e, #FF595E)";
                      } else {
                        color = val >= 0 ? "var(--bk-c-28a745, #28a745)" : "var(--bk-c-ff595e, #FF595E)";
                      }
                    }

                    return (
                      <td
                        key={s.label}
                        className="text-center"
                        style={{
                          fontWeight: row.bold || row.colored ? 600 : 400,
                          fontSize: row.bold ? "0.95rem" : "0.85rem",
                          color,
                          backgroundColor: s.isExpected
                            ? "rgba(25,130,196,0.05)"
                            : "transparent",
                          paddingTop: 8,
                          paddingBottom: 8,
                        }}
                      >
                        {row.negative ? `(${row.format(val)})` : row.format(val)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--bk-c-dee2e6, #dee2e6)" }}>
        <BarChart scenarios={enrichedScenarios} cmTarget={cmTarget} />
      </div>
    </div>
  );
};

export default ProFormaScenarioTable;
